import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { fileUrl } from "@/lib/format";
import { useServices, useSiteSettings } from "@/lib/cms";
import { getIcon } from "@/lib/icons";
import { useDocumentTitle } from "@/lib/use-document-title";
import { ScrollReveal } from "@/components/scroll-reveal";
import { HeroSlider } from "@/components/hero-slider";
import { CtaButton } from "@/components/cta-button";
import { MortgageAdvisorBox } from "@/components/mortgage-advisor";
import { PartnersSection } from "@/components/partners-section";
import type { Project, Service } from "@shared/schema";

// Default architectural placeholder background — replaced by an admin-set
// hero image as soon as one is uploaded via /admin/site-settings.
const DEFAULT_HERO_IMAGE =
  "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1920&q=85";

export function HomePage() {
  useDocumentTitle();
  const settings = useSiteSettings();
  const { home } = settings;

  const projectsQuery = useQuery({
    queryKey: ["projects"],
    queryFn: () => api<Project[]>("/api/projects", { withAuth: false }),
  });

  const servicesQuery = useServices();

  const heroImageUrl = home.heroImageKey
    ? fileUrl(home.heroImageKey)
    : DEFAULT_HERO_IMAGE;

  return (
    <div>
      {/* ─── Hero slider — intro slide + every published project ────── */}
      <div data-preview-section="hero">
      <HeroSlider
        projects={projectsQuery.data ?? []}
        fallbackImage={heroImageUrl}
        intro={{
          eyebrow: home.heroEyebrow,
          title: home.heroTitle,
          subtitle: home.heroSubtitle,
          primaryHref: "/projektek",
          primaryLabel: home.ctaProjectsText,
          primaryLink: home.ctaProjectsLink,
          secondaryHref: "/kapcsolat",
          secondaryLabel: home.ctaContactText,
          secondaryLink: home.ctaContactLink,
        }}
      />
      </div>

      {/* ─── Hitelügyintézés — felhívás + visszahívás-kérő űrlap ──────── */}
      <MortgageAdvisorBox />

      {/* ─── About preview with watermark ─────────────────────────────── */}
      <section
        id="about-preview"
        data-preview-section="services"
        className="relative py-24 lg:py-32 bg-white section-titled overflow-hidden"
      >
        <div className="section-watermark uppercase">otthon</div>
        <div className="container relative z-10 max-w-5xl">
          <ScrollReveal className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-normal uppercase text-brand-700 tracking-ultrawide leading-tight">
              {home.servicesTitle}{" "}
              <span className="text-accent">
                {settings.brand.shortName}
              </span>
            </h2>
          </ScrollReveal>

          <div className="grid lg:grid-cols-5 gap-10 items-center">
            <ScrollReveal className="lg:col-span-2 text-brand-500 leading-relaxed text-justify">
              <p className="whitespace-pre-line">
                {settings.about.intro}
              </p>
            </ScrollReveal>

            <ScrollReveal delay={1} className="lg:col-span-3">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
                {servicesQuery.data?.slice(0, 4).map((s, i) => (
                  <ServiceItem key={s.id} index={i + 1} service={s} />
                ))}
                {!servicesQuery.data && (
                  <div className="text-brand-400 col-span-full text-sm">
                    Betöltés…
                  </div>
                )}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ─── Projects 2x2 grid with watermark ─────────────────────────── */}
      <section
        data-preview-section="projects"
        className="relative py-24 lg:py-32 bg-brand-50 section-titled overflow-hidden"
      >
        <div className="section-watermark uppercase">projekt</div>
        <div className="container relative z-10">
          <ScrollReveal className="text-center mb-16">
            <span className="section-eyebrow">{home.projectsEyebrow}</span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-heading font-normal uppercase text-brand-700 tracking-ultrawide">
              {home.projectsTitle}
            </h2>
          </ScrollReveal>

          {projectsQuery.isLoading && (
            <div className="text-brand-500 text-center">Betöltés...</div>
          )}

          <div className="grid sm:grid-cols-2 gap-4 lg:gap-6 max-w-7xl mx-auto">
            {projectsQuery.data?.slice(0, 4).map((p, i) => (
              <ScrollReveal key={p.id} delay={(i % 3) as 0 | 1 | 2}>
                <ProjectShowcase project={p} />
              </ScrollReveal>
            ))}
            {projectsQuery.data?.length === 0 && (
              <div className="text-brand-500 col-span-full text-center">
                Jelenleg nincs publikált projekt.
              </div>
            )}
          </div>

          {projectsQuery.data && projectsQuery.data.length > 0 && (
            <ScrollReveal className="text-center mt-12">
              <Link href="/projektek" className="btn-secondary">
                Összes projekt <ArrowRight size={14} />
              </Link>
            </ScrollReveal>
          )}
        </div>
      </section>

      {/* ─── CTA strip ────────────────────────────────────────────────── */}
      <section data-preview-section="cta" className="bg-brand-900 text-white py-20">
        <div className="container">
          <ScrollReveal className="flex flex-col lg:flex-row items-center justify-between gap-8 text-center lg:text-left max-w-5xl mx-auto">
            <div>
              <h3 className="text-white font-heading uppercase tracking-widish text-xl lg:text-2xl">
                {home.ctaStripTitle}
              </h3>
              <p className="mt-2 text-white/70 text-sm">
                {home.ctaStripSubtitle}
              </p>
            </div>
            <CtaButton
              link={home.ctaStripLink}
              fallbackHref="/kapcsolat"
              className="btn-primary shrink-0"
            >
              {home.ctaStripButtonText} <ArrowRight size={14} />
            </CtaButton>
          </ScrollReveal>
        </div>
      </section>

      {/* ─── Partners logos — last section before footer ──────────────── */}
      <PartnersSection />
    </div>
  );
}

function ServiceItem({ service, index }: { service: Service; index: number }) {
  const Icon = getIcon(service.icon);
  return (
    <div className="flex items-start gap-4">
      <div className="shrink-0 w-12 h-12 grid place-items-center text-accent border border-brand-200 group-hover:border-accent transition-colors">
        <Icon size={22} strokeWidth={1.4} />
      </div>
      <div>
        <div className="font-semibold uppercase tracking-widish text-brand-700 text-sm">
          {String(index).padStart(2, "0")} · {service.title}
        </div>
        {service.description && (
          <p className="mt-1.5 text-xs text-brand-500 leading-relaxed">
            {service.description}
          </p>
        )}
      </div>
    </div>
  );
}

function ProjectShowcase({ project }: { project: Project }) {
  const STATUS_LABEL: Record<string, string> = {
    planning: "Tervezés alatt",
    construction: "Kivitelezés alatt",
    sales: "Értékesítés alatt",
    completed: "Befejezett",
  };
  return (
    <Link
      href={`/projektek/${project.slug}`}
      className="group relative block overflow-hidden bg-brand-200 aspect-[4/3]"
    >
      {project.coverImageKey ? (
        <img
          src={fileUrl(project.coverImageKey)}
          alt={project.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      ) : (
        <div className="w-full h-full grid place-items-center bg-gradient-to-br from-brand-300 via-brand-400 to-brand-600 text-white/30">
          <Building2 size={96} strokeWidth={0.8} />
        </div>
      )}

      {/* Bottom gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

      {/* Title top-left */}
      <div className="absolute inset-x-0 top-0 p-6 lg:p-8">
        <h3 className="text-white font-heading uppercase tracking-widish text-lg lg:text-xl text-shadow-strong">
          {project.name}
        </h3>
      </div>

      {/* Status text — vertical right edge */}
      <div
        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 text-[10px] font-semibold uppercase tracking-widish"
        style={{ writingMode: "vertical-rl", transform: "rotate(180deg) translateX(50%)" }}
      >
        {STATUS_LABEL[project.status]}
      </div>

      {/* Bottom info on hover */}
      <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8 text-white">
        {project.shortDescription && (
          <p className="text-sm text-white/90 line-clamp-2 mb-2 max-w-md">
            {project.shortDescription}
          </p>
        )}
        <div className="flex items-center gap-2 text-xs uppercase tracking-widish font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
          <span>Megnézem</span>
          <ArrowRight size={14} />
        </div>
      </div>
    </Link>
  );
}
